export type ProductType =
  | 'single-habitat-day-pass'
  | 'all-habitats-day-pass'
  | 'educator-week-pass'
  | 'explorer-pass'
  | 'educator-pass'
  | 'sponsorship'
  | 'tz-plus-week-pass';

/** The cards that unlock one named habitat, and so cannot be rendered without one. */
export type HabitatScopedProductType = 'single-habitat-day-pass' | 'sponsorship';

const HABITAT_SCOPED = new Set<ProductType>(['single-habitat-day-pass', 'sponsorship']);

/**
 * The single declaration of which cards need a habitat.
 *
 * Two places act on it and they must not disagree: `renderProductCard` drops such a card when it has
 * no habitat to name, and `rendersNothing` in `SpeedyFlow/core/products` refuses to open a step
 * whose every card would be dropped that way. Declared here, beside the vocabulary itself, because
 * `core/` is pure and cannot import the renderer while the renderer would form a cycle importing
 * `core/`.
 *
 * A type predicate rather than the set itself, so the renderer narrows to the two cards it can
 * actually build and needs no cast to do it.
 */
export const isHabitatScoped = (productType: ProductType): productType is HabitatScopedProductType =>
  HABITAT_SCOPED.has(productType);

/**
 * The closed vocabulary of offers a link may ask for, via the `ppt` query param. This is the only
 * input that selects products — `utm_source` attributes but never changes what is on sale (ADR
 * 0004). Adding an offer is deliberately a code change, so that editing a campaign URL cannot put
 * a new one into production.
 */
export type ProductPromptType =
  | 'explorer'
  | 'educator'
  | 'premium'
  | 'all-access'
  | 'day-pass'
  | 'single-habitat-day-pass'
  | 'sponsor'
  // Reachable only from a link that asks for it, or a gate that names it in code. Deliberately
  // absent from the history-based default pair, so launching it cannot change what any existing
  // visitor is offered.
  | 'tzplus'
  | 'general';

export interface ProductsPromptModalState {
  // Note: we have to use a boolean to represent the visibility state because
  // if checking the value which affect the UI, the modal dismissing animation
  // may not be played.
  visible?: boolean;
  habitatId?: string;
  primaryProductType?: ProductType;
  secondaryProductType?: ProductType;
  forEducator?: boolean; // for `all-habitats-day-pass` only, used to determine the product title and description
  redirectOnDismiss?: boolean;
  resetQueryParamsOnDismiss?: boolean;
  title?: string;
  dismissButtonLabel?: string;
  entryPoint?: string;
  /**
   * Offer id to apply at checkout, from the `ppto` query param (parsed beside `ppt` on the habitat
   * page). Redeemed — the day-pass checkout passes it as the `offer` field and the backend validates
   * eligibility. Lowercased at parse time (the id is case-insensitive).
   */
  offerId?: string;
}

export type SetProductsPromptModalStatePayload = Omit<ProductsPromptModalState, 'visible'> | null;
